import { Component, ErrorInfo, PropsWithChildren } from 'react'
import { logger, reportErrorDetails } from '$utils'

interface AppErrorBoundaryState {
  readonly hasError: boolean
  readonly error: Error | null
}

/**
 * A top-level error boundary for the React app which catches errors that have
 * escaped any higher-level boundaries. This will display a message to the user
 * explaining that something has gone wrong, but also log the error hopefully
 * in a way we can diagnose and fix things.
 */
class AppErrorBoundary extends Component<
  PropsWithChildren,
  AppErrorBoundaryState
> {
  state: AppErrorBoundaryState = {
    hasError: false,
    error: null,
  }

  static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    logger.error('React app top-level error boundary caught error: %o', error)
    logger.error('Component stack: %s', errorInfo.componentStack)

    reportErrorDetails(error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            flexDirection: 'column',
            fontFamily: 'Avenir, sans-serif',
            color: 'var(--title-color)',
            // These values match the header and sidenav dimensions. They're
            // hardcoded here to reduce external dependencies in this file as
            // much as possible (given it's the top-level error boundary).
            marginTop: '-64px',
            marginLeft: '230px',
          }}
        >
          <h2>Something went wrong</h2>
          <p>
            The application encountered an unexpected error. Try reloading the
            page to try again.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: '16px',
              marginBottom: '24px',
              padding: '8px 16px',
              borderRadius: '4px',
              backgroundColor: 'var(--palette-action-blue)',
              color: '#fff',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            Reload Page
          </button>
          <p>If this keeps happening please contact our support team.</p>
        </div>
      )
    }

    return this.props.children
  }
}

export default AppErrorBoundary
